import { useCallback } from 'react';
import { useParams, useRouteMatch } from 'react-router-dom';

import { useMenuModuleContext } from 'modules/menu/context';

import {
  BULK_IMAGE_UPLOAD_PAGE_PATH,
  ADD_CATEGORIES_PATH,
  GOOGLE_SHEETS_PAGE_PATH,
  IMAGE_GALLERY_PAGE_PATH,
  TRANSLATIONS_PAGE_PATH,
} from '../constants';
import { useSelectionInfoContext } from '../contexts/SelectionInfoContext';
import {
  ADD_TO_OFFERS_PATHNAME,
  BROWSE_IMAGES_PATHNAME,
  EDIT_IMAGES_PATHNAME,
} from '../pages/ListingManagerImageGalleryPage/constants';

type RouteMatchParams =
  | {
      feature: 'venue' | 'merchant';
      slug: string;
      brandId: undefined;
    }
  | {
      feature: 'enterprise';
      slug: string;
      brandId: string;
    };

export const useListingManagerRoutes = () => {
  const params = useParams<RouteMatchParams>();
  const { selection } = useSelectionInfoContext();

  const { url } = useRouteMatch();
  const { venue } = useMenuModuleContext();
  const isExperienceFlow = url.startsWith('/experience');
  const isVenueFeature = !!venue;
  const baseUrl =
    isVenueFeature && !isExperienceFlow
      ? `/${params.feature}/${params.slug}`
      : url.split('/shared-menu')[0].concat('/shared-menu');
  const getMenuEditorUrl = useCallback(
    (selectionIdOverride: string | undefined = undefined) => {
      if (isVenueFeature) {
        return `${baseUrl}${isExperienceFlow ? '' : '/menu-editor'}`;
      }

      return `${baseUrl}/${selectionIdOverride ?? selection?.id}/menu-editor`;
    },
    [baseUrl, isVenueFeature, selection?.id, isExperienceFlow],
  );

  const getSelectionUrl = useCallback(
    (selectionIdOverride: string | undefined = undefined) => {
      if (isVenueFeature) {
        return '';
      }

      return `${baseUrl}/${selectionIdOverride ?? selection?.id}`;
    },
    [baseUrl, isVenueFeature, selection?.id],
  );

  return {
    /**
     * For shared menus, the root page is the shared menu page `<brandBaseUrl/merchantBaseUrl>/shared-menu`
     * For venues, the root page is the venue page `/venue/<venueSlug>`
     */
    rootPage: () => baseUrl,
    selectionPage: (selectionIdOverride: string | undefined = undefined) => getSelectionUrl(selectionIdOverride),
    menuEditorPage: (selectionIdOverride: string | undefined = undefined) => getMenuEditorUrl(selectionIdOverride),
    menuEditorGoogleSheetsImportPage: () => `${getMenuEditorUrl()}/${GOOGLE_SHEETS_PAGE_PATH}`,
    menuEditorBulkImageUploadPage: () => `${getMenuEditorUrl()}/${BULK_IMAGE_UPLOAD_PAGE_PATH}`,
    menuEditorTranslationsPage: () => `${getMenuEditorUrl()}/${TRANSLATIONS_PAGE_PATH}`,
    menuEditorImageGalleryPage: () => `${getMenuEditorUrl()}/${IMAGE_GALLERY_PAGE_PATH}`,
    menuEditorAddCategoriesPage: () => `${getMenuEditorUrl()}/${ADD_CATEGORIES_PATH}`,
    menuEditorImageGalleryBrowseImagesPage: () =>
      `${getMenuEditorUrl()}/${IMAGE_GALLERY_PAGE_PATH}/${BROWSE_IMAGES_PATHNAME}`,
    menuEditorImageGalleryEditImagesPage: () =>
      `${getMenuEditorUrl()}/${IMAGE_GALLERY_PAGE_PATH}/${EDIT_IMAGES_PATHNAME}`,
    menuEditorImageGalleryAddToOffersPage: () =>
      `${getMenuEditorUrl()}/${IMAGE_GALLERY_PAGE_PATH}/${ADD_TO_OFFERS_PATHNAME}`,
  };
};
